import * as React from 'react';
import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import classnames from 'classnames';

import ActionButton from 'components/buttons/action-button/ActionButton';

import * as AdminPanel from '../admin-panel';
import BrandProfileApprovalActionsModal from './brand-profile-approval-actions-modal/BrandProfileApprovalActionsModal';

import { changeApprovalStatus } from '../../reducers/profilesByApprovalStatus';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';
import { fetchCampaignsWithUnreleasedPayouts, resetCampaignsWithUnreleasedPayouts } from '../../reducers/admin-profiles';

import { __t, applyTranslations, t } from 'utils/language';
import {
  AVAILABLE_STATE_TRANSITIONS,
  getApprovalStatus
} from 'utils/profile';

import type {
  BrandProfile
} from 'types';

applyTranslations(require('./brand-profile-admin-panel.i18n.json'));

type OwnProps = {
  profile: BrandProfile,
  onChange: () => void
};

type StateProps = {
  campaignsWithUnreleasedPayouts: Array<number>
};

type DispatchProps = {
  dispatchChangeApprovalStatus: (
    profile: BrandProfile,
    status: string,
    externalReason: string
  ) => Promise<void>,
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      innerContent: React.ReactNode,
      onConfirm: () => void,
      onCancel: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void,
  dispatchFetchCampaignsWithUnreleasedPayouts: (brandProfileId: number) => Promise<void>,
  dispatchResetCampaignsWithUnreleasedPayouts: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

type State = {
  externalReason: string
};

const MSG_KEY = 'profile_approval_actions';

class BrandProfileApprovalActions extends React.Component<Props, State> {
  static defaultProps = {
    onChange: () => {}
  };

  state = {
    externalReason: ''
  };

  componentWillUnmount() {
    const { dispatchResetCampaignsWithUnreleasedPayouts } = this.props;
    dispatchResetCampaignsWithUnreleasedPayouts();
  }

  changeApprovalStatus(status: string) {
    const {
      profile,
      campaignsWithUnreleasedPayouts,
      dispatchChangeApprovalStatus,
      dispatchHideConfirmationModal,
      dispatchShowConfirmationModal,
      onChange
    } = this.props;

    dispatchShowConfirmationModal({
      message: __t(`${MSG_KEY}.status_change_confirmation`),
      innerContent: !isEmpty(campaignsWithUnreleasedPayouts)
        ? <BrandProfileApprovalActionsModal
            campaignsWithUnreleasedPayouts={campaignsWithUnreleasedPayouts}
            updateExternalReasonState={this.updateExternalReasonState}
        />
        : null,
      onConfirm: () =>
        dispatchChangeApprovalStatus(profile, status, this.state.externalReason)
          .then(() => {
            this.updateExternalReasonState('');
            onChange();
          })
          .then(() => dispatchHideConfirmationModal()),
      onCancel: () => dispatchHideConfirmationModal()
    });
  }

  handleBrandProfileRejectionButtonClick(status: string) {
    const { profile, dispatchFetchCampaignsWithUnreleasedPayouts } = this.props;

    dispatchFetchCampaignsWithUnreleasedPayouts(profile.id)
      .then(() => this.changeApprovalStatus(status));
  }

  updateExternalReasonState = (nextExternalReason?: string | null) => {
    this.setState({
      externalReason: nextExternalReason || ''
    });
  };

  getProfileLink(id: number) {
    return `/content-creator/profiles/${id}`;
  }

  render() {
    const { profile } = this.props;
    const approvalStatus = getApprovalStatus(profile);
    const label = t(`profile_approval_status_${approvalStatus || ''}`) || approvalStatus;

    if (!approvalStatus) {
      return null;
    }

    const availableStateTransitions = AVAILABLE_STATE_TRANSITIONS[approvalStatus] || [];
    const classes = classnames(
      'brand-profile-approval-panel__approval',
      `brand-profile-approval-panel__approval--${approvalStatus}`
    );

    return (
      <AdminPanel.Block>
        <AdminPanel.BlockTitle>
          {__t(`${MSG_KEY}.title`)}:<span className={classes}>{label}</span>
        </AdminPanel.BlockTitle>
        {
          availableStateTransitions.map((statusToTransit) => (
            <ActionButton
              key={`profile-approval-actions-profile-button-change-to-${statusToTransit}`}
              onClick={() => {
                this.handleBrandProfileRejectionButtonClick(statusToTransit);
              }}
            >
              {__t(`${MSG_KEY}.status_change_button.${statusToTransit}`)}
            </ActionButton>
          ))
        }
      </AdminPanel.Block>
    );
  }
}

const mapStateToProps = ({ adminProfiles }): StateProps => ({
  campaignsWithUnreleasedPayouts: adminProfiles.campaignsWithUnreleasedPayouts.campaignIds
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchChangeApprovalStatus: (profile, status, externalReason) =>
    dispatch(changeApprovalStatus(profile, 'brand', status, externalReason)),

  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal()),

  dispatchFetchCampaignsWithUnreleasedPayouts: (brandProfileId: number) => (
    dispatch(fetchCampaignsWithUnreleasedPayouts(brandProfileId))
  ),

  dispatchResetCampaignsWithUnreleasedPayouts: () => dispatch(resetCampaignsWithUnreleasedPayouts())
});

export default connect(mapStateToProps, mapDispatchToProps)(BrandProfileApprovalActions);
